import React, { useState, useContext, useEffect, useMemo } from "react";
import Card from "react-bootstrap/Card";
import { Spinner, Button } from "react-bootstrap";
import "Pages/SbJobDetails/SbJobDetails.scss";
import { PAICProjectContext } from "State/PAICProjectContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getPAICJDs } from "Api/apiService";
import JobTitleOption from "Components/Common/JobTitleOption";
import FormField from "Components/Common/FormField";
import CoreMarkdownEditor from "Components/SbMarkdownEditor/CoreMarkdownEditor";
import { AuthContext } from "State/AuthContext";
import analytics from 'Utils/analytics';
import SbDownloadMarkdownPdf from "Components/SbJobDetailsComponent/SbJobDetailsSubComponents/SbDownloadMarkdownPdf";

const SbPACPeopleJDView = () => {
    const navigate = useNavigate();
    //const { projectId } = useContext(PAICProjectContext);
    const location = useLocation();
    const routeSelectionId = location.state?.selectionId;
    
    const { selectionId: contextSelectionId } = useContext(PAICProjectContext);
    const { email } = useContext(AuthContext);
    const projectId = routeSelectionId || contextSelectionId;

    const [loading, setLoading] = useState(false);
    const [paicJds, setPaicJds] = useState([]);
    const [selectedJdId, setSelectedJdId] = useState("");
    const [viewMode, setViewMode] = useState("VIEW");

    useEffect(() => {
        const fetchData = async () => {
            if (!projectId) {
                return;
            }

            setLoading(true);
            
            // Track People JD view opened
            analytics.trackPAICPeopleJDViewOpened({
                from: 'pac_people_jd_view',
                email: email,
                description: 'Opened People JD view'
            });
            
            try {
                const response = await getPAICJDs(projectId);
                setPaicJds(response || []);

                if (Array.isArray(response) && response.length > 0) {
                    setSelectedJdId(response[0].sb_paic_jd_unique_id);
                }

            } catch (err) {
                console.error("Error fetching PAIC People JDs:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [projectId]);

    const formatJDsToMarkdown = (data) => {
        if (!data) return "";
        const humanJd = data.paic_jd?.human_jd;
        if (!humanJd) return "No JD data available.";

        const title = humanJd.title || "Job Description";
        const summary = humanJd.summary || "";
        const sections = humanJd.sections || [];

        let md = `# ${title}\n\n`;
        
        if (summary) {
            md += `**Summary:** ${summary}\n\n`;
        }

        sections.forEach(section => {
            md += `### ${section.heading}\n\n`;
            if (section.content && Array.isArray(section.content)) {
                section.content.forEach(line => {
                    md += `- ${line}\n`;
                });
            }
            md += `\n`;
        });

        return md;
    };

    const selectedJdData = useMemo(() => {
        return paicJds.find(jd => jd.sb_paic_jd_unique_id === selectedJdId);
    }, [paicJds, selectedJdId]);

    const markdownData = useMemo(() => {
        return formatJDsToMarkdown(selectedJdData);
    }, [selectedJdData]);

    const aiAgentOptions = useMemo(() => {
        return paicJds.map(jd => ({
            value: jd.sb_paic_jd_unique_id,
            label: jd.paic_jd?.human_jd?.title || "Untitled JD"
        }));
    }, [paicJds]);

    const handlePagination = (type) => {
        if (!aiAgentOptions.length) return;
        
        let index = aiAgentOptions.findIndex((opt) => opt.value === selectedJdId);
        if (type === "PREV") {
            index = index <= 0 ? aiAgentOptions.length - 1 : index - 1;
        } else if (type === "NEXT") {
            index = index >= aiAgentOptions.length - 1 ? 0 : index + 1;
        }
        
        setSelectedJdId(aiAgentOptions[index].value);
        
        // Track pagination
        analytics.trackPAICPeopleJDPagination({
            from: 'pac_people_jd_view',
            email: email,
            direction: type,
            description: `People JD pagination: ${type}`
        });
    };

    const handleCloseButton = () => {
        // Track close documents
        analytics.trackPAICCloseDocuments({
            from: 'pac_people_jd_view',
            email: email,
            description: 'Closed People JD view'
        });
        
        navigate("/people-plus-ai/pac-group-agents");
    };

    const jobTitleOptions = [
        {
            id: 1,
            isActive: false,
            name: "Jobs Library"
        },
        {
            id: 2,
            isActive: false,
            name: "Agents Library"
        },
        {
            id: 3,
            name: "People + AI",
            isActive: true
        },
        {
            id: 4,
            isActive: false,
            name: "Skills Bridger"
        },
        {
            id: 5,
            isActive: false,
            name: "Learning Catalyst"
        },
        {
            id: 6,
            isActive: false,
            name: "Talent Mobility"
        }
    ]

    return (
        <Card className="border-0 rounded-0 shadow-sm full-height-card h-100">
            <Card.Body className="d-flex flex-column">

                {/* Header Section */}
                <div className="d-flex align-items-center justify-content-between">
                    <Card.Title className="text-start jobs-heading">
                        Job Details: People + AI Job Descriptions
                        <span>
                            <small className="text-body-tertiary jobs-description ms-3">
                                View Mode: Read Only
                            </small>
                        </span>
                    </Card.Title>
                    <div className="icon-wrapper">
                        <svg
                            onClick={handleCloseButton}
                            className="icon"
                            width="30"
                            height="30"
                            viewBox="0 0 30 30"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ cursor: 'pointer' }}
                        >
                            <rect width="30" height="30" rx="3" fill="#008080" />
                            <path
                                d="M10.3333 20.8332L9.16667 19.6665L13.8333 14.9998L9.16667 10.3332L10.3333 9.1665L15 13.8332L19.6667 9.1665L20.8333 10.3332L16.1667 14.9998L20.8333 19.6665L19.6667 20.8332L15 16.1665L10.3333 20.8332Z"
                                fill="white"
                            />
                        </svg>
                    </div>
                </div>

                {/* Custom Tabs with divs - VISUAL ONLY, Interactivity Disabled */}
                <div className="custom-tabs d-flex flex-column flex-grow-1 mb-3">
                    {/* Top bar tabs */}
                    <div className="tab-titles align-items-center d-flex justify-content-between flex-wrap">
                        <div className="d-flex flex-wrap" style={{ cursor: 'default' }}>
                            <JobTitleOption jobTitleOptions={jobTitleOptions}/>
                        </div>

                        <div className="icon-wrapper d-flex align-items-center gap-2">
                            <div onClick={()=>handlePagination("PREV")}>
                                <svg
                                width="30"
                                height="30"
                                viewBox="0 0 30 30"
                                style={{ cursor: "pointer" }}
                                >
                                    <rect width="30" height="30" rx="3" fill="#008080" />
                                    <path
                                        d="M17 9 L11 15 L17 21"
                                        stroke="white"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                                
                            <div className="flex-shrink-0" style={{ width: '280px' }}>
                                <FormField
                                    type="select"
                                    value={selectedJdId}
                                    onChange={(val) => { setSelectedJdId(val); }}
                                    placeholder="Select Job Description"
                                    disabled={false}
                                    options={aiAgentOptions}
                                    isClearable={false}
                                />                                
                            </div>

                            <div onClick={()=>handlePagination("NEXT")}>
                                <svg
                                width="30"
                                height="30"
                                viewBox="0 0 30 30"
                                style={{ cursor: "pointer" }}
                                >
                                    <rect width="30" height="30" rx="3" fill="#008080" />
                                    <path
                                        d="M13 9 L19 15 L13 21"
                                        stroke="white"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                   
                    {/* Content Display Area */}
                    <div className="tab-content flex-grow-1" style={{ overflow: 'hidden' }}>
                        <div className="tab-pane d-flex h-100 flex-column active">
                            
                             <div className="grey-bar d-flex justify-content-between align-items-center">
                                {/* Left side */}
                                <div className="jd-version-dropdown d-flex align-items-center">
                                    <div className="me-3 text-start">
                                        <select className="dropdown">
                                            <option>JD Version - 1</option>
                                        </select>

                                        {/* <div className="text-start">
                                            <small className="text-body-tertiary jobs-description">
                                            Modified on Jan 10, 2026
                                            <br />
                                            Current Owner: John Doe
                                            </small>
                                        </div> */}
                                    </div>
                                </div>

                                {/* Right side icon */}
                                {viewMode !== "EDIT" && (
                                    <div className="icon-group">
                                        <div
                                            className="icon-wrapper"
                                            data-tooltip="Comparison Summary"
                                            style={{ cursor: "pointer" }}
                                        >
                                            <svg
                                            className="icon"
                                            width="30"
                                            height="30"
                                            viewBox="0 0 30 30"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            >
                                            <rect
                                                width="30"
                                                height="30"
                                                rx="3"
                                                fill="white"
                                            />
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M13 8C13.55 8 14 8.45 14 9C14 9.55 13.55 10 13 10H8C7.45 10 7 9.55 7 9C7 8.45 7.45 8 8 8H13ZM13 12C13.55 12 14 12.45 14 13C14 13.55 13.55 14 13 14H8C7.45 14 7 13.55 7 13C7 12.45 7.45 12 8 12H13ZM13 16C13.55 16 14 16.45 14 17C14 17.55 13.55 18 13 18H8C7.45 18 7 17.55 7 17C7 16.45 7.45 16 8 16H13ZM13 20C13.55 20 14 20.45 14 21C14 21.55 13.55 22 13 22H8C7.45 22 7 21.55 7 21C7 20.45 7.45 20 8 20H13ZM22 8C22.55 8 23 8.45 23 9C23 9.55 22.55 10 22 10H17C16.45 10 16 9.55 16 9C16 8.45 16.45 8 17 8H22ZM22 12C22.55 12 23 12.45 23 13C23 13.55 22.55 14 22 14H17C16.45 14 16 13.55 16 13C16 12.45 16.45 12 17 12H22ZM22 16C22.55 16 23 16.45 23 17C23 17.55 22.55 18 22 18H17C16.45 18 16 17.55 16 17C16 16.45 16.45 16 17 16H22ZM22 20C22.55 20 23 20.45 23 21C23 21.55 22.55 22 22 22H17C16.45 22 16 21.55 16 21C16 20.45 16.45 20 17 20H22Z"
                                                fill="#8F95B2"
                                            />
                                            </svg>
                                        </div>
                                        <div
                                            className={`icon-wrapper me-4`}
                                            data-tooltip="Difference Checker"
                                            style={{
                                            cursor: "pointer",
                                            }}
                                        >
                                            {" "}
                                            <svg
                                            className=""
                                            width="30"
                                            height="30"
                                            viewBox="0 0 30 30"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            >
                                            <rect
                                                width="30"
                                                height="30"
                                                rx="3"
                                                fill="white"
                                            />
                                            <path
                                                d="M14.0832 24.3761V22.3319H9.14784C8.72556 22.3319 8.37325 22.1908 8.09092 21.9084C7.80859 21.6261 7.66712 21.2735 7.6665 20.8506V9.14659C7.6665 8.72432 7.80798 8.37201 8.09092 8.08968C8.37386 7.80734 8.72617 7.66587 9.14784 7.66526H14.0832V5.62109H14.9998V24.3761H14.0832ZM17.0092 8.58193V7.66526H21.0288C21.4504 7.66526 21.8027 7.80673 22.0857 8.08968C22.3686 8.37262 22.5098 8.72493 22.5092 9.14659V20.8515C22.5092 21.2732 22.368 21.6255 22.0857 21.9084C21.8033 22.1914 21.451 22.3325 21.0288 22.3319H17.0092V21.4153H21.0288C21.1693 21.4153 21.2986 21.3566 21.4165 21.2393C21.5344 21.1219 21.5931 20.9924 21.5925 20.8506V9.14659C21.5925 9.00543 21.5338 8.87587 21.4165 8.75793C21.2992 8.63998 21.1699 8.58132 21.0288 8.58193H17.0092ZM17.0092 15.4569V14.5403H20.2175V15.4569H17.0092ZM17.0092 12.2486V11.3319H20.2175V12.2486H17.0092ZM10.8748 18.6653H14.0832V17.7486H10.8748V18.6653ZM10.8748 15.4569H14.0832V14.5403H10.8748V15.4569ZM10.8748 12.2486H14.0832V11.3319H10.8748V12.2486Z"
                                                fill="#8790A4"
                                            />
                                            </svg>
                                        </div>
                                        <div
                                            className="icon-wrapper"
                                            data-tooltip="Edit"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                setViewMode("EDIT");
                                                
                                                // Track edit mode
                                                analytics.trackPAICPeopleJDEditMode({
                                                    from: 'pac_people_jd_view',
                                                    email: email,
                                                    description: 'Entered edit mode for People JD'
                                                });
                                            }}
                                        >
                                            <svg
                                            width="30"
                                            height="30"
                                            viewBox="0 0 30 30"
                                            fill="none"
                                            >
                                                <rect
                                                    width="30"
                                                    height="30"
                                                    rx="3"
                                                    fill="white"
                                                />
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M19.3536 7.64645C19.1583 7.45118 18.8417 7.45118 18.6465 7.64645L16.6484 9.64452C16.6477 9.64516 16.6471 9.6458 16.6464 9.64645C16.6458 9.64709 16.6452 9.64774 16.6445 9.64839L8.64646 17.6464C8.58238 17.7105 8.53692 17.7908 8.51494 17.8787L7.51494 21.8787C7.47234 22.0491 7.52227 22.2294 7.64646 22.3536C7.77065 22.4777 7.95089 22.5277 8.12128 22.4851L12.1213 21.4851C12.2092 21.4631 12.2895 21.4176 12.3536 21.3536L20.3536 13.3536L22.3536 11.3536C22.5488 11.1583 22.5488 10.8417 22.3536 10.6464L19.3536 7.64645ZM20 12.2929L21.2929 11L19 8.70711L17.7071 10L20 12.2929ZM17 10.7071L19.2929 13L11.7444 20.5485L8.68719 21.3128L9.45149 18.2556L17 10.7071Z"
                                                    fill={
                                                        "#8790A4"
                                                    }
                                                />
                                            </svg>
                                        </div>
                                        <div
                                            className="icon-wrapper"
                                            data-tooltip={
                                            "Revise this JD (4-5 minutes approx)"
                                            }
                                            style={{
                                            cursor:"pointer",
                                            }}
                                        >
                                            <svg
                                            className=""
                                            width="30"
                                            height="30"
                                            viewBox="0 0 30 30"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            >
                                            <rect
                                                width="30"
                                                height="30"
                                                rx="3"
                                                fill="white"
                                            />
                                            <g clipPath="url(#clip0_1332_10976)">
                                                <path
                                                d="M15.5713 7.57031H9.857C9.5539 7.57031 9.26321 7.69072 9.04888 7.90505C8.83455 8.11937 8.71415 8.41007 8.71415 8.71317V18.9989L7.57129 22.4275L12.1427 21.2846H21.2856C21.5887 21.2846 21.8794 21.1642 22.0937 20.9499C22.308 20.7355 22.4284 20.4448 22.4284 20.1417V14.4275"
                                                stroke={
                                                    "#8790A4"
                                                } // Grey out stroke
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                />
                                                <path
                                                d="M16.5575 16.2968L13.1289 16.9139L13.7003 13.4397L19.106 8.05681C19.2123 7.94969 19.3387 7.86466 19.478 7.80664C19.6172 7.74862 19.7666 7.71875 19.9175 7.71875C20.0683 7.71875 20.2177 7.74862 20.357 7.80664C20.4963 7.86466 20.6227 7.94969 20.7289 8.05681L21.9403 9.26823C22.0475 9.37448 22.1325 9.50088 22.1905 9.64015C22.2485 9.77941 22.2784 9.92879 22.2784 10.0797C22.2784 10.2305 22.2485 10.3799 22.1905 10.5192C22.1325 10.6584 22.0475 10.7848 21.9403 10.8911L16.5575 16.2968Z"
                                                stroke={
                                                    "#8790A4"
                                                } // Grey out stroke
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1332_10976">
                                                <rect
                                                    width="16"
                                                    height="16"
                                                    fill="white"
                                                    transform="translate(7 7)"
                                                />
                                                </clipPath>
                                            </defs>
                                            </svg>
                                        </div>
                                        <div
                                            className="icon-wrapper"
                                            data-tooltip="Download"
                                            onClick={() => {
                                                analytics.trackDownloadJD({
                                                    from: "pac_people_jd_view",
                                                    email: email,
                                                    description: "Downloaded People JD",
                                                });
                                            }}
                                        >
                                            <SbDownloadMarkdownPdf markdownContent={markdownData} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="editor-container h-100 position-relative d-flex flex-column" style={{ overflow: 'hidden' }}>
                                {loading && (
                                    <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                )}
                                {!loading && (
                                    <>
                                    {viewMode === "EDIT" ? (
                                        <div className="human-editor-container flex-grow-1">
                                            {/* Internal Header */}
                                            <div className="editor-header grey-bar d-flex justify-content-between align-items-center">
                                                <div className="text-start">
                                                    <h5
                                                    className="mb-0 jobs-heading"
                                                    style={{ fontSize: "16px" }}
                                                    >
                                                    Editing Draft
                                                    </h5>
                                                    <small className="text-body-tertiary jobs-description">
                                                    Prepare your draft and assign an approver to
                                                    submit.
                                                    </small>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setViewMode("VIEW");
                                                        
                                                        // Track cancel
                                                        analytics.trackPAICPeopleJDCancel({
                                                            from: 'pac_people_jd_view',
                                                            email: email,
                                                            description: 'Cancelled People JD edit'
                                                        });
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>

                                            {/* SCROLLABLE BODY */}
                                            <div className="blocks-list">
                                                <div id="content-to-download">
                                                    <CoreMarkdownEditor
                                                        initialMarkdown={markdownData}
                                                        viewMode="EDIT"
                                                        key={markdownData}
                                                    />
                                                </div>
                                            </div>

                                            <div className="footer-actions">
                                                {/* <div className="d-flex align-items-center gap-4">
                                                    <ApproverSelector
                                                    defaultValue={null} // Default null for fresh drafts/published edits
                                                    />
                                                    <div className="status-indicator">
                                                        <span className="text-warning small fw-bold">
                                                        ⚠ Assign an HR manager to submit
                                                        </span>
                                                    </div>
                                                </div> */}

                                                <div className="d-flex gap-2">
                                                    <Button
                                                    variant="primary"
                                                    size="lg"
                                                    disabled={true}
                                                    className="px-5 shadow-sm d-flex align-items-center"
                                                    style={{
                                                        backgroundColor: "#008080",
                                                        border: "none",
                                                    }}
                                                    >
                                                        Submit
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        ) : (
                                        <CoreMarkdownEditor
                                            initialMarkdown={markdownData}
                                            viewMode={viewMode}
                                            key={markdownData}
                                        />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default SbPACPeopleJDView;
